import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Footer from './Footer';
import '../Styling/AboutUs.css';
import { aboutImages } from '../constants/images';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';


const AboutUs = () => {
  useEffect(() => {
    document.title = 'About Us - QuantifyLogic';
    window.scrollTo(0, 0);
  }, []);

  const achievements = [
    { number: '200+', text: 'Projects Delivered', icon: '🎯' },
    { number: '50+', text: 'Expert Professionals', icon: '👥' },
    { number: '95%', text: 'Client Satisfaction', icon: '⭐' }
  ];

  const values = [
    {
      title: 'Innovation',
      description: 'We constantly push boundaries to deliver cutting-edge solutions.',
      icon: '💡'
    },
    {
      title: 'Excellence',
      description: 'Quality and perfection are at the heart of everything we do.',
      icon: '⭐'
    },
    {
      title: 'Integrity',
      description: 'We maintain the highest standards of honesty and transparency.',
      icon: '🤝'
    },
    {
      title: 'Client Focus',
      description: 'Your success is our priority. We are committed to exceeding expectations.',
      icon: '🎯'
    }
  ];

  return (
    <motion.div
      className="about-us-wrapper"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="service-navigation-wrapper">
        <button 
          className="nav-button back-button"
          onClick={() => window.history.back()}
          aria-label="Go back"
        >
          <FaArrowLeft />
          <span>Back</span>
        </button>
      </div>
      <div className="about-container">
        {/* Hero Section */}
        <motion.section 
          className="about-hero"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="hero-content">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Transforming Ideas into Digital Reality
            </motion.h1>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              We are a team of passionate technologists dedicated to delivering innovative solutions that drive business success.
            </motion.p>
          </div>
        </motion.section>

        {/* Mission Section */}
        <section className="mission-section">
          <div className="section-container">
            <motion.div
              className="mission-content"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="mission-image">
                <img src={aboutImages.mission} alt="Our Mission - Corporate Excellence" />
              </div>
              <div className="mission-text">
                <h2>Our Mission</h2>
                <p>
                  To empower businesses through innovative technology solutions, delivering excellence in 
                  software development, digital transformation, and IT consulting services. We strive to be 
                  the catalyst that transforms your vision into reality while maintaining the highest 
                  standards of quality and customer satisfaction.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Achievements Section */}
        <section className="achievements-section">
          <div className="section-container">
            <div className="achievements-grid">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  className="achievement-card"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <span className="achievement-icon">{achievement.icon}</span>
                  <h3>{achievement.number}</h3>
                  <p>{achievement.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="values-section">
          <div className="section-container">
            <motion.div
              className="values-content"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="values-image">
                <img src={aboutImages.values} alt="Our Core Values - Team Collaboration" />
              </div>
              <div className="values-text">
                <h2>Our Core Values</h2>
                <div className="values-grid">
                  {values.map((value, index) => (
                    <motion.div
                      key={index}
                      className="value-card"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                    >
                      <span className="value-icon">{value.icon}</span>
                      <h3>{value.title}</h3>
                      <p>{value.description}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="why-choose-section">
          <div className="section-container">
            <motion.div
              className="why-choose-content"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="team-image">
                <img src={aboutImages.whyChooseUs} alt="Why Choose Us - Professional Excellence" />
              </div>
              <div className="features-content">
                <h2>Why Choose QuantifyLogic?</h2>
                <div className="features-grid">
                  <div className="feature">
                    <h3>Expert Team</h3>
                    <p>Highly skilled professionals with deep industry expertise</p>
                  </div>
                  <div className="feature">
                    <h3>Custom Solutions</h3>
                    <p>Tailored approaches to meet your unique business needs</p>
                  </div>
                  <div className="feature">
                    <h3>Quality Assurance</h3>
                    <p>Rigorous testing and quality control processes</p>
                  </div>
                  <div className="feature">
                    <h3>24/7 Support</h3>
                    <p>Round-the-clock assistance and maintenance</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        

        {/* CTA Section */}
        <section className="about-cta">
          <div className="section-container">
            <motion.div
              className="cta-content"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="cta-image">
                <img src={aboutImages.cta} alt="Start Your Journey - Business Growth" />
              </div>
              <div className="cta-text">
                <h2>Ready to Start Your Digital Journey?</h2>
                <p>Let's discuss how we can help transform your business</p>
                <Link to="/contactus" className="cta-button">Get in Touch</Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer />
    </motion.div>
  );
};

export default AboutUs; 